/**
 * Types pour les articles (produits et services)
 */

export type ArticleType = 'produit' | 'service' | 'restaurant';

// Miroir de backend/src/types/article.ts. Predicats en ALLOW-LIST (jamais `!== 'service'`) :
// tout type futur est stockless et hors catalogue par defaut -> fail-secure.
export const hasStock = (type: ArticleType): boolean => type === 'produit';

export const isStockless = (type: ArticleType): boolean => !hasStock(type);

export const isSellableOnline = (type: ArticleType): boolean =>
  type === 'produit' || type === 'restaurant';

// Distinct de `isSellableOnline` malgre une valeur identique aujourd'hui : "achetable en ligne"
// n'est pas "livrable". Le jour ou un type devient vendable mais non livrable (bon cadeau
// dematerialise, prestation en visio), reutiliser `isSellableOnline` afficherait "Livraison
// disponible" sur un produit immateriel sans qu'aucun test ne le detecte.
export const isDeliverable = (type: ArticleType): boolean =>
  type === 'produit' || type === 'restaurant';

export const ARTICLE_TYPE_LABELS: Record<ArticleType, string> = {
  produit: 'Produit',
  service: 'Service',
  restaurant: 'Restaurant',
};

export type ArticleStatus = 'active' | 'inactive' | 'archived';

export interface Article {
  id: string;
  reference: string;
  name: string;
  abbreviation?: string;
  description?: string;
  descriptionHTML?: string;
  type: ArticleType;
  categoryIds: string[]; // Relations N:N avec catégories
  suggestionGroupIds?: string[]; // Groupes de suggestion à afficher avec ce produit
  categoryName?: string; // DEPRECATED: pour compatibilité
  priceHT: number;
  prixAchat?: number; // Prix d'achat (optionnel, défaut: priceHT)
  tvaRate: number;
  priceTTC: number;
  status: ArticleStatus;
  visibleFrontOffice: boolean;
  visibleBackOffice: boolean;
  stockAlertThreshold?: number;
  warrantyDays?: number;
  images?: string[];
  mainImageIndex?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ArticleWithStock extends Article {
  stockQuantity?: number;
  stockAvailable?: number;
  isInStock: boolean;
}

export interface ArticleCategory {
  id: string;
  name: string;
  description?: string;
  isGroup?: boolean; // true = groupe de suggestion, false/undefined = catégorie
}

// Types pour le système de suggestions e-commerce
export interface SuggestionGroup {
  group: ArticleCategory;
  articles: ArticleWithStock[];
}

export interface CatalogProductsResponse {
  products: ArticleWithStock[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}
